import { Alert, Card, SimpleGrid, Skeleton, VStack } from '@chakra-ui/react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { formatCurrency } from '@packages/core/utils/currency-formatter';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import { DateRange } from '../../common/reservation-analytic-service';
import SkeletonCard from '../../common/skeleton-card';
import MetricCard from '../../reservation/component/metric-card';
import useOnlineOrderOverview from '../logic/use-online-order-overview';

export default function OnlineOrderOverviewReportCard(props: { dateRange: DateRange }) {
  const branch = useFocusedBranchContext();
  const { t } = useTranslate();
  const company = useFocusedCompanyContext();

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: props.dateRange.startDate,
    endDate: props.dateRange.endDate,
  });

  useEffect(() => {
    setDateRange({
      startDate: props.dateRange.startDate,
      endDate: props.dateRange.endDate,
    });
  }, [props.dateRange]);

  const { analytics, isLoading } = useOnlineOrderOverview(branch.branch?.id ?? '', dateRange);

  if (isLoading) {
    return (
      <VStack gap={4} align="stretch">
        <Skeleton height="40px" />
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </SimpleGrid>
      </VStack>
    );
  }

  if (!analytics) {
    return (
      <Alert.Root status="info" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Alert.Title>No data available</Alert.Title>
      </Alert.Root>
    );
  }

  const { current, trends } = analytics;

  return (
    <Card.Root>
      <Card.Header>
        <Card.Title fontWeight={'semibold'} fontSize={'xl'}>
          {t('onlineOrder.report.overview', 'Overview')}
        </Card.Title>
      </Card.Header>
      <Card.Body p={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} w={'full'}>
          <MetricCard
            title={t('onlineOrder.report.overview.totalOrders', 'Total Orders')}
            value={current.totalOrders}
            trend={trends.totalOrders}
            subtitle={t(
              'onlineOrder.report.overview.totalOrders.subtitle',
              'Total orders placed in the selected period',
            )}
          />
          <MetricCard
            title={t('onlineOrder.report.overview.totalRevenue', 'Total Revenue')}
            value={formatCurrency(current.totalRevenue, company.settings.currency)}
            trend={trends.totalRevenue}
            subtitle={t(
              'onlineOrder.report.overview.totalRevenue.subtitle',
              'Total revenue generated in the selected period',
            )}
          />
          <MetricCard
            title={t('onlineOrder.report.overview.canceledOrders', 'Canceled Orders')}
            value={current.canceledOrders}
            trend={trends.canceledOrders}
            subtitle={t(
              'onlineOrder.report.overview.canceledOrders.subtitle',
              'Total orders canceled in the selected period',
            )}
          />
          <MetricCard
            title={t('onlineOrder.report.overview.averageRevenuePerOrder', 'Average Revenue per Order')}
            value={formatCurrency(current.averageRevenuePerOrder, company.settings.currency)}
            trend={trends.averageRevenuePerOrder}
            subtitle={t(
              'onlineOrder.report.overview.averageRevenuePerOrder.subtitle',
              'Average revenue generated per order',
            )}
          />
        </SimpleGrid>
      </Card.Body>
    </Card.Root>
  );
}
